import { cn } from "@/lib/utils"

/* ds-typo-ignore-file — compact hero KPI/trust strip: intentional sub-DS label sizes (14px value / 10px label), shared by the membership + ad-LP heroes */

/**
 * Bottom-pinned trust / KPI strip for dark photo heroes — the membership hero
 * treatment (value line + optional muted label, divided columns). Extracted so
 * the membership and ad-LP heroes render the exact same component instead of
 * each hand-rolling it. Place inside a `relative` hero section; it pins itself
 * to the foot. Items with no `label` render the value line only.
 */
export function HeroTrustStrip({
  items,
  align = "center",
  compactMobile = false,
  className,
}: {
  items: { value: string; label?: string }[]
  /** "center" matches the membership hero; "left" aligns with a left-anchored hero. */
  align?: "center" | "left"
  /** On mobile only: hide the sub-labels and cap the value width so each value
   *  wraps to ~two lines for a uniform column height. Desktop is unchanged. */
  compactMobile?: boolean
  className?: string
}) {
  const left = align === "left"
  return (
    <div
      className={cn(
        "absolute right-0 bottom-0 left-0 z-10 pb-7 md:pb-8",
        className
      )}
    >
      <div
        className={cn(
          "mx-auto",
          left ? "max-w-[1440px] px-6 md:px-10" : "max-w-[860px] px-4 md:px-10"
        )}
      >
        <div
          className={cn(
            "grid divide-x divide-white/15 md:gap-0",
            left && "max-w-[760px]",
            items.length === 3 ? "grid-cols-3" : "grid-cols-2"
          )}
        >
          {items.map((item, i) => (
            <div
              key={item.value}
              className={cn(
                "flex flex-col px-2",
                left ? "text-left first:pl-0" : "text-center md:text-left",
                i > 0 && "md:pl-6",
                i < items.length - 1 && "md:pr-6"
              )}
            >
              {/* ds-typo-ignore — compact KPI strip; mobile uses
                  sub-token sizes to keep three columns on one row. */}
              <span
                className={cn(
                  "text-[14px] leading-[15px] font-medium text-white/85 md:text-[18px] md:leading-[22px] md:text-white/75",
                  compactMobile && "max-w-[84px] md:max-w-none"
                )}
              >
                {item.value}
              </span>
              {item.label && (
                <span
                  className={cn(
                    "md:type-label-md mt-1.5 text-[10px] leading-[11px] tracking-[0.05em] text-white/40 uppercase md:mt-0.5 md:tracking-normal md:normal-case",
                    compactMobile && "hidden md:block"
                  )}
                >
                  {item.label}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
